import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { GridViewWebComponent } from '../../../grid/gridView';
import { GridViewMetadata, ViewConfiguration, ViewType } from '../../../grid/gridView/types';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { WEB_COMPONENT_GRID_VIEW } from '../../../webcomponents.constants';
import { GridConfig } from './GridConfig';
import { Column, ColumnKeys, Dependencies } from '../types';
import {
  ActionType,
  getActionLabels,
  getAllWebCategoryLabels,
  ScheduleItem,
  SubSchedule,
  WebCategory,
} from '../../webAccessControlSchedulerApp/types';
import { FormGroup } from '../../../ui/gzForm/formGroup/form-group';
import { GzFormFieldWebComponent } from '../../../ui/gzForm/formField';
import { Subscription } from 'rxjs';
import { GzEnumFilterWebComponent } from '../../../ui/gzEnumFilter';
import { EnumListItem } from '../../../ui/gzEnumList/types';
import { SelectOption } from '../../../ui/gzDropdown/types';
import { WeekDays } from '../../../types/common/schedulerTypes';
import { GzTextWebComponent } from '../../../ui/gzText';
import { GzTimePickerWebComponent } from '../../../ui/gzTimePicker';
import { TimeIntervalValidator } from '../../../ui/gzForm/validators/time-interval.validator';
import { RequiredValidator } from '../../../ui/gzForm/validators/required.validator';
import _ from 'lodash';
import { ElementClass, elementClassListAdd, elementClassListRemove } from '../../../utils/htmlElement.utils';
import { GzInfoBoxWebComponent } from '../../../ui/gzInfoBox';
import { Rights } from '@nirvana/shared/constants';
import { translateFn } from '../../../utils/translator.helper';

export class WebAccessControlSchedulerFormView extends BaseWebComponentView {
  public dom: {
    $grid: GridViewWebComponent<Column, ColumnKeys>;
    $warningMessageSubschedules: GzInfoBoxWebComponent;
    $btnCancel: GzButtonWebComponent;
    $btnSave: GzButtonWebComponent;
    $btnUpdateScheduler: GzButtonWebComponent;
    $btnAddNewScheduler: GzButtonWebComponent;
    $addNewSchedulerLabel: HTMLElement;
    $scheduleTitle: HTMLElement;
    $subscheduleTitle: HTMLElement;
    $hostElement: HTMLElement;
    $subscheduleNameFormField: GzFormFieldWebComponent;
    $allWebsitesFormField: GzFormFieldWebComponent;
    $selectBoxCategories: GzEnumFilterWebComponent;
    $checkboxShowAlertsFormField: GzFormFieldWebComponent;
    btnToggleSwitchAllowChange: GzFormFieldWebComponent;
    $anotherCompanyMessage: HTMLElement;
    $multiselectStartWithFieldForm: GzFormFieldWebComponent;
    $selectBoxActionFieldForm: GzFormFieldWebComponent;
    $andLabel: GzTextWebComponent;
    $timePickerStart: GzFormFieldWebComponent;
    $timePickerEnd: GzFormFieldWebComponent;
    $inputScheduleNameFormField: GzFormFieldWebComponent;
  };
  private scheduleFormGroup: FormGroup;
  private scheduleFormGroupSubscription: Subscription | null;
  private subscheduleFormGroup: FormGroup;
  private subscheduleFormGroupSubscription: Subscription | null;
  public dependencies: Dependencies;
  private schedule: ScheduleItem;
  private subschedule: SubSchedule;
  private isFormEnabled: boolean;
  private readonly subscheduleLimit: number = 20;

  public getDomElements(): void {
    this.dom.$scheduleTitle = this.shadow.querySelector('#scheduleTitle');
    this.dom.$inputScheduleNameFormField = this.shadow.querySelector('#inputScheduleNameFormField');
    this.dom.$grid = this.shadow.querySelector(WEB_COMPONENT_GRID_VIEW);
    this.dom.$warningMessageSubschedules = this.shadow.querySelector('#warningMessageSubschedules');

    // subschedule form
    this.dom.$subscheduleTitle = this.shadow.querySelector('#subscheduleTitle');
    this.dom.$subscheduleNameFormField = this.shadow.querySelector('#subscheduleNameFormField');
    this.dom.$allWebsitesFormField = this.shadow.querySelector('#allWebsites');
    this.dom.$selectBoxCategories = this.shadow.querySelector('#selectBoxCategories');
    this.dom.$checkboxShowAlertsFormField = this.shadow.querySelector('#checkboxShowAlerts');
    this.dom.btnToggleSwitchAllowChange = this.shadow.querySelector('#btnToggleSwitchAllowChange');
    this.dom.$anotherCompanyMessage = this.shadow.querySelector('#anotherCompanyMessage');
    this.dom.$selectBoxActionFieldForm = this.shadow.querySelector('#selectBoxActionFieldForm');
    this.dom.$multiselectStartWithFieldForm = this.shadow.querySelector('#multiselectStartWithFieldForm');
    this.dom.$andLabel = this.shadow.querySelector('#andLabel');
    this.dom.$timePickerStart = this.shadow.querySelector('#timePickerStart');
    this.dom.$timePickerEnd = this.shadow.querySelector('#timePickerEnd');
    this.dom.$btnUpdateScheduler = this.shadow.querySelector('#btnUpdateScheduler');
    this.dom.$btnAddNewScheduler = this.shadow.querySelector('#btnAddNewScheduler');
    this.dom.$addNewSchedulerLabel = this.shadow.querySelector('#addNewSchedulerLabel');

    this.dom.$btnCancel = this.shadow.querySelector('#btnCancel');
    this.dom.$btnSave = this.shadow.querySelector('#btnSave');
    this.dom.$hostElement = this.shadow;

    this.getTranslations();

    if (!Application.Main.hasAllRights([Rights.MANAGE_POLICIES_WRITE])) {
      this.dom.$btnSave.disabled = true;
      this.dom.$btnUpdateScheduler.disabled = true;
      this.dom.$btnAddNewScheduler.disabled = true;
    }
  }

  async buildView(data) {
    this.schedule = this.shadow.host.schedule ?? this.getDefaultSchedule();
    this.dependencies.schedule = this.schedule;
    this.isFormEnabled =
      Application.Main.hasAllRights([Rights.MANAGE_POLICIES_WRITE]) &&
      (!this.dependencies?.schedule?.created?.userId ||
        this.dependencies?.schedule.created?.userId === Application.Main.user._id ||
        this.dependencies?.schedule.allowChangeByOtherUsers);
    this.dependencies.isFormEnabled = this.isFormEnabled;
    this.unsubscribeObservers();
    this.setTranslations();
    const { viewMetaData } = new GridConfig().getDataForView(this.dependencies);
    this.createScheduleFormGroup();
    this.createSubscheduleFormGroup();
    this.buildGrid(viewMetaData);
    this.resetScheduleFormFields();
    this.resetSubScheduleFormFields();
  }

  private getDefaultSchedule(): ScheduleItem {
    return {
      _id: '',
      name: '',
      company: {
        id: Application.Main.getActiveCompany().id,
        name: Application.Main.getActiveCompany().name,
      },
      allowChangeByOtherUsers: false,
      subschedules: [],
    };
  }

  private getDefaultSubSchedule(): SubSchedule {
    return {
      name: '',
      enableDetailedAlerts: true,
      action: ActionType.BLOCK,
      days: [],
      allWebsites: true,
      categories: [],
      timeInterval: {
        startHour: 0,
        startMinute: 0,
        endHour: 23,
        endMinute: 59,
      },
    };
  }

  public resetSubScheduleFormFields() {
    this.subschedule = this.getDefaultSubSchedule();
    this.setSubscheduleFormData(this.subschedule);
    this.dom.$subscheduleNameFormField.resetField();
    this.dom.$multiselectStartWithFieldForm.resetField();
  }

  public resetScheduleFormFields() {
    this.setScheduleFormData(this.schedule);
    this.dom.$inputScheduleNameFormField.resetField();
  }

  private createScheduleFormGroup() {
    this.schedule.company = {
      id: Application.Main.getActiveCompany().id,
      name: Application.Main.getActiveCompany().name,
    };

    this.dom.$inputScheduleNameFormField.value = this.schedule?.name;
    this.dom.$inputScheduleNameFormField.Input.setAttribute('placeholder', translateFn('Scheduler name'));
    this.dom.$inputScheduleNameFormField.Validators = [RequiredValidator(this.commonStrings.requiredField)];

    this.scheduleFormGroup = new FormGroup({
      name: this.dom.$inputScheduleNameFormField,
      allowChangeByOtherUsers: this.dom.btnToggleSwitchAllowChange,
    });

    if (this.isFormEnabled) {
      this.scheduleFormGroupSubscription = this.scheduleFormGroup.valid$.subscribe({
        next: valid => {
          if (!Application.Main.hasAllRights([Rights.MANAGE_POLICIES_WRITE])) {
            this.dom.$btnSave.disabled = true;
          } else {
            this.dom.$btnSave.disabled = !(valid && !!this.schedule.subschedules.length);
          }
        },
      });
    }
  }

  private createSubscheduleFormGroup() {
    this.dom.$timePickerStart.label = translateFn('Between');
    this.dom.$subscheduleNameFormField.Input.setAttribute('placeholder', translateFn('Schedule name'));

    this.dom.$timePickerEnd.Validators = [
      TimeIntervalValidator(
        this.dom.$timePickerStart.Input as GzTimePickerWebComponent,
        translateFn('Start time has to be before end time.')
      ),
    ];
    this.dom.$subscheduleNameFormField.Validators = [RequiredValidator(this.commonStrings.requiredField)];
    this.dom.$multiselectStartWithFieldForm.Validators = [
      RequiredValidator(translateFn('You must select one day of the week')),
    ];

    this.subscheduleFormGroup = new FormGroup({
      name: this.dom.$subscheduleNameFormField,
      allWebsites: this.dom.$allWebsitesFormField,
      enableDetailedAlerts: this.dom.$checkboxShowAlertsFormField,
      allowChangeByOtherUsers: this.dom.btnToggleSwitchAllowChange,
      action: this.dom.$selectBoxActionFieldForm,
      days: this.dom.$multiselectStartWithFieldForm,
      timeInterval: new FormGroup({
        start: this.dom.$timePickerStart,
        end: this.dom.$timePickerEnd,
      }),
    });

    if (this.isFormEnabled) {
      this.subscheduleFormGroupSubscription = this.subscheduleFormGroup.valid$.subscribe({
        next: valid => {
          if (!Application.Main.hasAllRights([Rights.MANAGE_POLICIES_WRITE])) {
            this.dom.$btnUpdateScheduler.disabled = true;
            this.dom.$btnAddNewScheduler.disabled = true;
          } else {
            this.dom.$btnUpdateScheduler.disabled = !(valid && _.isNumber(this.subschedule?.index));
            this.dom.$btnAddNewScheduler.disabled = !(
              valid && Number(this.dependencies?.schedule?.subschedules.length) < this.subscheduleLimit
            );
          }
        },
      });
    }

    this.setDisabledPropertyToFormFields();
    this.checkSubschedulesLimit();

    this.dom.$selectBoxCategories.label = translateFn('Categories');
    this.dom.$selectBoxCategories.filterOptions = this.getEnumListItem(getAllWebCategoryLabels());
    this.dom.$selectBoxCategories.searchPlaceholderString = translateFn('Search categories');
    this.dom.$selectBoxCategories.placeholder = translateFn('Select categories');
    this.dom.$selectBoxCategories.confirmActionButtonString = translateFn('Apply');
    this.dom.$selectBoxCategories.noSearchResultsString = translateFn('No results found');
    this.dom.$selectBoxCategories.listTitle = translateFn('All');
    this.dom.$selectBoxCategories.allItemsLabel = translateFn('All categories');
    this.dom.$selectBoxCategories.canSelectAll = true;
    this.dom.$selectBoxCategories.allowSelectNone = true;
    this.dom.$selectBoxCategories.searchMaxLength = 40;

    this.dom.$allWebsitesFormField.Input.setAttribute('label', translateFn('All websites'));
    this.dom.$checkboxShowAlertsFormField.Input.setAttribute('label', translateFn('Show detailed alerts on client'));
    this.dom.btnToggleSwitchAllowChange.Input.innerText = translateFn('Allow other users to change this scheduler');

    this.dom.$selectBoxActionFieldForm.label = translateFn('Action');
    this.dom.$selectBoxActionFieldForm.Input.setAttribute(
      'options',
      JSON.stringify(this.getSelectOptions(getActionLabels()))
    );

    const daysArray = Object.keys(WeekDays).filter(x => isNaN(Number(x)));
    const options = Array.from(daysArray, element => ({
      label: this.translations.scheduler.weekdays.shortNames[element],
      value: parseInt(WeekDays[element], 10),
    }));
    this.dom.$multiselectStartWithFieldForm.label = translateFn('Starting with');
    this.dom.$multiselectStartWithFieldForm.Input.setAttribute('options', JSON.stringify(options));
  }

  private buildGrid(viewMetaData: GridViewMetadata<Column, ColumnKeys>): void {
    const grid: GridViewWebComponent<Column, ColumnKeys> = this.dom.$grid;
    const defaultViewProps: ViewConfiguration<Column, ColumnKeys> = {
      compactView: viewMetaData.isCompactViewChecked,
      displayedColumns: viewMetaData.columns,
      filters: viewMetaData.filters,
      name: '',
      pageSize: viewMetaData.pageSize,
      type: ViewType.DEFAULT,
      id: viewMetaData.viewId,
    };
    grid.propertiesSet = false;
    for (const [key, value] of Object.entries(viewMetaData)) {
      grid[key] = value;
    }
    grid.defaultViewProps = defaultViewProps;
    grid.propertiesSet = true;
    grid.injectDependencies(this.dependencies);
    grid.start().catch(console.error);
  }

  private setDisabledPropertyToFormFields() {
    if (this.isFormEnabled) {
      this.setDisabledComponents(false);
    } else {
      this.setDisabledComponents(true);
    }

    if (!this.schedule.created?.userId || this.schedule.created?.userId === Application.Main.user._id) {
      elementClassListRemove(this.dom.btnToggleSwitchAllowChange, ElementClass.HIDDEN);
      elementClassListAdd(this.dom.$anotherCompanyMessage, ElementClass.HIDDEN);
    } else {
      elementClassListAdd(this.dom.btnToggleSwitchAllowChange, ElementClass.HIDDEN);

      if (this.schedule.allowChangeByOtherUsers) {
        elementClassListAdd(this.dom.$anotherCompanyMessage, ElementClass.HIDDEN);
      } else {
        elementClassListRemove(this.dom.$anotherCompanyMessage, ElementClass.HIDDEN);
      }
    }

    if (!Application.Main.hasAllRights([Rights.MANAGE_POLICIES_WRITE])) {
      this.dom.$anotherCompanyMessage.textContent = translateFn('Additional rights required to save this scheduler.');
      elementClassListRemove(this.dom.$anotherCompanyMessage, ElementClass.HIDDEN);
    }
  }

  private setDisabledComponents(disable: boolean) {
    this.dom.$selectBoxCategories.disabled = disable;
    this.scheduleFormGroup.setDisabled(disable);
    this.subscheduleFormGroup.setDisabled(disable);
  }

  public setScheduleFormData(schedule: ScheduleItem) {
    this.scheduleFormGroup.value = {
      name: schedule.name,
      allowChangeByOtherUsers: schedule.allowChangeByOtherUsers,
    };
  }

  public setSubscheduleFormData(subSchedule: SubSchedule) {
    this.subschedule = subSchedule;
    if (!Application.Main.hasAllRights([Rights.MANAGE_POLICIES_WRITE])) {
      this.dom.$btnUpdateScheduler.disabled = true;
      this.dom.$btnAddNewScheduler.disabled = true;
    } else {
      this.dom.$btnUpdateScheduler.disabled = !_.isNumber(this.subschedule.index);
    }

    this.dom.$selectBoxCategories.filterOptions = this.getEnumListItem(
      getAllWebCategoryLabels(),
      subSchedule.categories
    );
    this.subscheduleFormGroup.value = {
      name: subSchedule.name,
      allWebsites: subSchedule.allWebsites,
      enableDetailedAlerts: subSchedule.enableDetailedAlerts,
      action: subSchedule.action,
      days: subSchedule.days,
      timeInterval: {
        start: {
          hours: subSchedule.timeInterval.startHour,
          minutes: subSchedule.timeInterval.startMinute,
        },
        end: {
          hours: subSchedule.timeInterval.endHour,
          minutes: subSchedule.timeInterval.endMinute,
        },
      },
    };
  }

  public getSubscheduleFormData(): SubSchedule {
    this.subschedule.categories = this.dom.$selectBoxCategories.getCheckedValues() as Array<number>;
    const formValues: object = Object.assign({}, this.subscheduleFormGroup.value);
    const startHour = formValues['timeInterval'].start.hours,
      startMinute = formValues['timeInterval'].start.minutes,
      endHour = formValues['timeInterval'].end.hours,
      endMinute = formValues['timeInterval'].end.minutes;
    this.subschedule.timeInterval = {
      startHour: startHour,
      startMinute: startMinute,
      endHour: endHour,
      endMinute: endMinute,
    };
    delete formValues['timeInterval'];
    return Object.assign(this.subschedule, formValues);
  }

  public checkSubschedulesLimit() {
    if (!this.dependencies.schedule?.subschedules) {
      return;
    }

    if (!this.dependencies.schedule.subschedules.length) {
      (this.dom.$grid.parentElement as HTMLElement).style.height = '0px';
      elementClassListAdd(this.dom.$grid, ElementClass.HIDDEN);
    } else {
      const heightGrid = 33 + this.dependencies.schedule.subschedules.length * 32;
      (this.dom.$grid.parentElement as HTMLElement).style.height = heightGrid + 'px';
      elementClassListRemove(this.dom.$grid, ElementClass.HIDDEN);
    }

    if (this.dependencies.schedule.subschedules.length === this.subscheduleLimit) {
      if (
        this.schedule.created?.userId &&
        this.schedule.created?.userId !== Application.Main.user._id &&
        !this.schedule.allowChangeByOtherUsers
      ) {
        // don't display limit message for users that are not owner and don't have rights to changes
        elementClassListAdd(this.dom.$warningMessageSubschedules, ElementClass.HIDDEN);
      } else {
        elementClassListRemove(this.dom.$warningMessageSubschedules, ElementClass.HIDDEN);
      }
    } else {
      elementClassListAdd(this.dom.$warningMessageSubschedules, ElementClass.HIDDEN);
    }

    if (!Application.Main.hasAllRights([Rights.MANAGE_POLICIES_WRITE])) {
      this.dom.$btnSave.disabled = true;
    } else {
      this.dom.$btnSave.disabled = !(this.schedule.subschedules.length && this.scheduleFormGroup.valid);
    }
    this.dom.$btnAddNewScheduler.disabled = !(
      this.subscheduleFormGroup.valid &&
      Number(this.dependencies?.schedule?.subschedules.length) < this.subscheduleLimit
    );
  }

  public getScheduleFormData(): ScheduleItem {
    return Object.assign(this.schedule, this.scheduleFormGroup.value);
  }

  public getSubscheduleFormGroup(): FormGroup {
    return this.subscheduleFormGroup;
  }

  public getEnumListItem(
    keyValueMap: Record<number, string>,
    selectedValues: Array<WebCategory | number> = []
  ): EnumListItem[] {
    const labels: EnumListItem[] = [];

    for (const [value, label] of Object.entries(keyValueMap)) {
      labels.push({
        label: label,
        value: Number(value),
        selected: selectedValues.indexOf(Number(value)) !== -1,
      });
    }
    return labels.sort((a, b) => a.label.localeCompare(b.label));
  }

  public getSelectOptions(keyValueMap: Record<number, string>): SelectOption[] {
    const labels: SelectOption[] = [];

    for (const [value, label] of Object.entries(keyValueMap)) {
      labels.push({
        title: label,
        value: Number(value),
      });
    }
    return labels;
  }

  unsubscribeObservers() {
    this.subscheduleFormGroup?.unsubscribe();
    this.subscheduleFormGroupSubscription?.unsubscribe();
    this.scheduleFormGroup?.unsubscribe();
    this.scheduleFormGroupSubscription?.unsubscribe();
  }

  private setTranslations(): void {
    this.dom.$scheduleTitle.textContent = translateFn('Web Access Control Scheduler');
    this.dom.$subscheduleTitle.textContent = translateFn('Web Category Schedule');
    this.dom.$btnCancel.textContent = translateFn('Cancel');
    this.dom.$btnSave.textContent = translateFn('Save#preserve state');
    this.dom.$andLabel.textContent = translateFn('and');
    this.dom.$btnUpdateScheduler.textContent = translateFn('update schedule');
    this.dom.$addNewSchedulerLabel.textContent = translateFn('add new schedule');
    this.dom.$anotherCompanyMessage.textContent = translateFn(
      'Cannot save this scheduler because it belongs to another company.'
    );
    this.dom.$warningMessageSubschedules.textContent = translateFn(
      'The limit of schedules have been reached. No more schedules can be added.'
    );
  }

  getTranslations() {
    this.translations = {
      common: this.stringsTranslator.getCommonStrings(),
      scheduler: this.stringsTranslator.getSchedulerStrings(),
    };
  }

  public handleAllWebsitesChange(isChecked: any) {
    this.dom.$selectBoxCategories.disabled = isChecked;
    if (!isChecked) {
      return;
    }

    // clear categories if checkbox is selected
    this.dom.$selectBoxCategories.filterOptions = this.dom.$selectBoxCategories.filterOptions.map(option => {
      option.selected = false;
      return option;
    });
  }
}
